import {
  Active,
  closestCenter,
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  getClientRect,
  MouseSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy
} from '@dnd-kit/sortable';
import findIndex from 'lodash/findIndex';
import React, { useCallback, useMemo, useRef, useState } from 'react';

import { TextButton } from '@ds';
import { DSPlusIcon } from '@ds-icons';
import SmallLoader from '@shared/components/SmallLoader';
import {
  DashboardSection,
  IDashboardSection,
  LayoutItem
} from './DashboardSection';
import { generateEmptySection, getSectionById } from './utils';

import styles from './DashboardSection.module.scss';

type DashboardSectionsProps<SectionItem> = {
  loadingItems?: boolean;
  onAddItemClick: (id: string) => void;
  onSectionsChange: (sections: IDashboardSection<SectionItem>[]) => void;
  renderSectionItems: (
    id: string,
    items: SectionItem[],
    layout: LayoutItem[]
  ) => React.ReactNode;
  scrollContainer?: HTMLElement;
  sections: IDashboardSection<SectionItem>[];
};

export function DashboardSections<SectionItem>({
  loadingItems = false,
  onAddItemClick,
  onSectionsChange,
  renderSectionItems,
  scrollContainer = window.document.documentElement,
  sections
}: DashboardSectionsProps<SectionItem>) {
  const scrollSectionIdRef = useRef<string>('');
  const dragTopCoordinate = useRef<number>(0);

  const [active, setActive] = useState<Active | null>(null);

  const measuringConfig = useMemo(
    () => ({
      draggable: {
        measure: (element: HTMLElement) => {
          const rect = getClientRect(element);

          if (dragTopCoordinate.current === 0) {
            dragTopCoordinate.current = rect.top;
          }

          return {
            ...rect,
            top: dragTopCoordinate.current
          };
        }
      }
    }),
    []
  );

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        // need to prevent items rerender after user click on drag handle,
        // the drag will be initialized when user will move mouse at least for 1 pixel
        distance: 1
      }
    })
  );

  const activeSection = useMemo(
    () => getSectionById<SectionItem>(sections, (active?.id ?? '').toString()),
    [active?.id, sections]
  );

  const handleDragCancel = useCallback(() => {
    setActive(null);
  }, []);

  const handleDragStart = useCallback((event: DragStartEvent) => {
    dragTopCoordinate.current = 0;
    setActive(event.active);
  }, []);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;

      if (over && active.id !== over.id) {
        const oldIndex = sections.findIndex(
          section => section.id === active.id
        );
        const newIndex = sections.findIndex(section => section.id === over.id);

        onSectionsChange(arrayMove(sections, oldIndex, newIndex));
        handleDragCancel();
      }
    },
    [handleDragCancel, onSectionsChange, sections]
  );

  const handleAddSection = useCallback(() => {
    onSectionsChange([...sections, generateEmptySection<SectionItem>()]);
  }, [onSectionsChange, sections]);

  const handleDeleteSection = useCallback(
    (id: string) => {
      let updatedSections: IDashboardSection<SectionItem>[] = [];

      // special handling that not allow to delete last section (it's actually re-creating automatically )
      if (sections.length === 1) {
        updatedSections.push(generateEmptySection<SectionItem>());
      } else {
        updatedSections = sections.filter(section => section.id !== id);
      }

      onSectionsChange(updatedSections);
    },
    [onSectionsChange, sections]
  );

  const handleAddSectionById = useCallback(
    (id: string, shift: number) => {
      const index = findIndex(sections, section => section.id === id);
      const updatedSections = sections.slice();
      const newSection = generateEmptySection<SectionItem>();

      updatedSections.splice(index + shift, 0, newSection);
      onSectionsChange(updatedSections);
      scrollSectionIdRef.current = newSection.id;
    },
    [onSectionsChange, sections]
  );

  const handleSectionChange = useCallback(
    (id: string, updatedSection: Partial<IDashboardSection<SectionItem>>) => {
      onSectionsChange(
        sections.map(section => {
          if (section.id === id) {
            return { ...section, ...updatedSection };
          }

          return section;
        })
      );
    },
    [onSectionsChange, sections]
  );

  const scrollTo = useCallback(
    node => {
      if (scrollContainer && node) {
        const TOOLBAR_HEIGHT = 64;
        const top =
          scrollContainer.scrollTop +
          node.getBoundingClientRect().top -
          TOOLBAR_HEIGHT;

        scrollContainer.scroll({ top, behavior: 'smooth' });
      }
    },
    [scrollContainer]
  );

  if (loadingItems) {
    return (
      <div className={styles.loadingItems}>
        <SmallLoader
          primaryMessage="Loading..."
          secondaryMessage={<span>Building a layout...</span>}
        />
      </div>
    );
  }

  const renderItemSection = ({
    isDragPreview = false,
    section,
    scrollToRef
  }: {
    isDragPreview?: boolean;
    section: IDashboardSection<SectionItem>;
    scrollToRef?: (node: HTMLElement) => void;
  }) => {
    return (
      <DashboardSection
        onAddItemClick={onAddItemClick}
        onAddSectionById={handleAddSectionById}
        onDeleteSection={handleDeleteSection}
        isDragPreview={isDragPreview}
        key={section.id}
        onSectionChange={handleSectionChange}
        renderItems={renderSectionItems}
        scrollToRef={scrollToRef}
        section={section}
      />
    );
  };

  return (
    <div className={styles.itemSectionsWrapper}>
      <DndContext
        sensors={sensors}
        measuring={measuringConfig}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        <SortableContext
          items={sections}
          strategy={verticalListSortingStrategy}
        >
          <div className={styles.sectionsContainer}>
            {sections.map(section => {
              let scrollToRef;
              if (section.id === scrollSectionIdRef.current) {
                scrollToRef = scrollTo;
                scrollSectionIdRef.current = '';
              }

              return renderItemSection({
                section,
                scrollToRef
              });
            })}
          </div>
        </SortableContext>
        <DragOverlay dropAnimation={null}>
          {activeSection &&
            renderItemSection({
              section: activeSection,
              isDragPreview: true
            })}
        </DragOverlay>
      </DndContext>
      <div className={styles.addSectionContainer}>
        <TextButton
          type="primary"
          size="medium"
          PrefixIcon={<DSPlusIcon />}
          onClick={handleAddSection}
          data-test="add-section-button"
        >
          Add section
        </TextButton>
      </div>
    </div>
  );
}
